<script setup lang="ts">
import { useI18n } from 'vue-i18n'

// Public showcase of the loading indicators. Intentionally not linked from the navigation.
definePage({
  meta: {
    requiresAuth: false,
  },
})

const { t } = useI18n()
const { setPageTitle } = useBreadcrumbs()

setPageTitle('pages.loaders.title')

const introKey = ref(0)

const loaders = [
  { key: 'flat', code: '<AppLoader size="lg" />', props: { variant: 'flat' } },
  { key: '3d', code: '<AppLoader size="lg" variant="3d" />', props: { variant: '3d' } },
] as const

const spinners = [
  { key: 'flat', code: '<AppSpinner size="lg" />' },
  { key: '3d', code: '<AppSpinner size="lg" variant="3d" />' },
  { key: 'orbit', code: '<AppSpinner size="lg" variant="orbit" />' },
  { key: 'dots', code: '<AppSpinner size="lg" variant="dots" />' },
  { key: 'cube', code: '<AppSpinner size="lg" variant="cube" />' },
  { key: 'helix', code: '<AppSpinner size="lg" variant="helix" />' },
] as const
</script>

<template>
  <div class="mx-auto max-w-5xl space-y-10 py-6">
    <header class="space-y-2">
      <h1 class="text-3xl font-bold">{{ t('pages.loaders.title') }}</h1>
      <p class="max-w-2xl text-muted">{{ t('pages.loaders.intro') }}</p>
    </header>

    <section class="space-y-4">
      <div>
        <h2 class="text-xl font-semibold">{{ t('pages.loaders.logo.title') }}</h2>
        <p class="text-sm text-muted">{{ t('pages.loaders.logo.description') }}</p>
      </div>
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <figure
          v-for="loader in loaders"
          :key="loader.key"
          class="flex flex-col items-center gap-5 rounded-xl border border-default bg-default p-6"
        >
          <div class="flex h-28 items-center">
            <AppLoader size="lg" :variant="loader.props.variant" />
          </div>
          <figcaption class="space-y-1 text-center">
            <p class="font-medium">{{ t(`pages.loaders.logo.variants.${loader.key}`) }}</p>
            <code class="text-xs text-muted">{{ loader.code }}</code>
          </figcaption>
        </figure>
        <figure
          class="flex flex-col items-center gap-5 rounded-xl border border-default bg-default p-6"
        >
          <div :key="introKey" class="flex h-28 items-center gap-6">
            <AppLoader size="lg" intro />
            <AppLoader size="lg" variant="3d" intro />
          </div>
          <figcaption class="space-y-1 text-center">
            <p class="font-medium">{{ t('pages.loaders.logo.variants.intro') }}</p>
            <code class="text-xs text-muted">&lt;AppLoader size="lg" intro /&gt;</code>
            <div>
              <UButton
                :label="t('pages.loaders.replay')"
                icon="i-lucide-rotate-ccw"
                size="xs"
                variant="soft"
                class="mt-2"
                @click="introKey++"
              />
            </div>
          </figcaption>
        </figure>
      </div>
    </section>

    <section class="space-y-4">
      <div>
        <h2 class="text-xl font-semibold">{{ t('pages.loaders.spinner.title') }}</h2>
        <p class="text-sm text-muted">{{ t('pages.loaders.spinner.description') }}</p>
      </div>
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <figure
          v-for="spinner in spinners"
          :key="spinner.key"
          class="flex flex-col items-center gap-5 rounded-xl border border-default bg-default p-6"
        >
          <div class="flex h-28 items-center">
            <AppSpinner size="lg" :variant="spinner.key" />
          </div>
          <figcaption class="space-y-1 text-center">
            <p class="font-medium">{{ t(`pages.loaders.spinner.variants.${spinner.key}`) }}</p>
            <code class="text-xs text-muted">{{ spinner.code }}</code>
          </figcaption>
        </figure>
      </div>
    </section>
  </div>
</template>
